import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import * as api from 'api/api'
import { AccessRole, ReportListItem } from 'api/types'
import { useAuth } from 'auth/AuthContext'
import { useActiveProject } from 'components/app-shell/context/ActiveProjectContext'
import { Icon } from 'components/Icon'
import { Button, Card, EmptyState, ErrorState, IconButton, SelectField, Spinner, StatusBadge } from 'components/ui'
import { ROLE_LABELS, addDays, daysBetween, formatDate, formatDateTime, monthBounds, monthKey, monthTitle, monthsBetween, weekStart } from 'lib/format'
import { useAction, useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'
import { activeLocale } from 'lib/region'
import { printWeekday } from 'pages/report/print-locale'

type Mode = 'week' | 'month' | 'custom'

const MAX_RANGE_DAYS = 400

const ROLE_COPY: Record<AccessRole, { titleAr: string; titleEn: string; bodyAr: string; bodyEn: string; tone: 'info' | 'warning' }> = {
  ADMIN: {
    bodyAr: 'كل تقارير المشروع المختار، بالإضافة إلى سجل الاعتمادات لآخر 12 شهراً وقالب إكسل المستخدم في كل ملف.',
    bodyEn: 'Every report in the selected project, plus the approval log for the last 12 months and the Excel template used for each file.',
    titleAr: 'تصدير على مستوى النظام',
    titleEn: 'System-wide export',
    tone: 'info',
  },
  PROJECT_MANAGER: {
    bodyAr: 'الحزمة تشمل المسودات والتقارير بانتظار الاعتماد. أي تقرير غير معتمد يُعلَّم «مسودة» داخل الملف.',
    bodyEn: 'The package includes drafts and reports waiting for approval. Any report that is not approved is marked "Draft" in the file.',
    titleAr: 'كل تقارير المشروع',
    titleEn: 'All project reports',
    tone: 'info',
  },
  SITE_ENGINEER: {
    bodyAr: 'نزّل تقريراً واحداً بصيغة Excel أو PDF، أو حزمة للفترة. التقارير غير المعتمدة تُعلَّم «مسودة» داخل الملف.',
    bodyEn: 'Download one report as Excel or PDF, or a package for the period. Reports that are not approved are marked "Draft" in the file.',
    titleAr: 'تقارير تستطيع فتحها في هذا المشروع',
    titleEn: 'Reports you can open in this project',
    tone: 'info',
  },
  SUPERVISOR: {
    bodyAr: 'الإشراف يصدّر التقارير المعتمدة فقط. المسودات وما زال قيد الاعتماد لا تدخل الحزمة ولا تظهر في الجدول.',
    bodyEn: 'Supervisor exports approved reports only. Drafts and reports still waiting for approval are left out of the package and the table.',
    titleAr: 'التقارير المعتمدة فقط',
    titleEn: 'Approved reports only',
    tone: 'warning',
  },
}

const clampDate = (date: string, min: string, max: string) => (date < min ? min : date > max ? max : date)

export const ExportsPage = () => {
  const { user } = useAuth()
  const activeProject = useActiveProject()
  const { data: projects, error: projectsError, loading: projectsLoading, reload: reloadProjects } = useAsync(api.fetchProjects, [])
  const { data: today } = useAsync(api.fetchToday, [])
  const { data: template, error: templateError, loading: templateLoading, reload: reloadTemplate } = useAsync(
    () => (user?.isAdmin ? api.fetchTemplateInfo() : Promise.resolve(undefined)),
    [user?.isAdmin]
  )
  const [projectId, setProjectId] = useState('')
  const [mode, setMode] = useState<Mode>('month')
  const [month, setMonth] = useState<string | null>(null)
  const [week, setWeek] = useState<string | null>(null)
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const { busy, run } = useAction()

  useEffect(() => {
    if (projectId || !projects?.length) return
    const preferred = projects.find((project) => project.id === activeProject?.id) ?? projects[0]
    setProjectId(preferred.id)
    setCustomFrom(preferred.startDate)
    setCustomTo(preferred.endDate)
  }, [activeProject?.id, projectId, projects])

  const project = projects?.find((item) => item.id === projectId)
  const role = project?.myRole
  const copy = role ? ROLE_COPY[role] : null

  const months = useMemo(
    () => (project ? monthsBetween(project.startDate, project.endDate) : []),
    [project]
  )
  const weeks = useMemo(() => {
    if (!project) return []
    const list: string[] = []
    for (let day = weekStart(project.startDate); day <= project.endDate; day = addDays(day, 7)) list.push(day)
    return list
  }, [project])

  const anchor = project && today ? clampDate(today, project.startDate, project.endDate) : project?.startDate
  const activeMonth = month ?? (anchor ? monthKey(anchor) : months[0])
  const activeWeek = week ?? (anchor ? weekStart(anchor) : weeks[0])

  const range = (() => {
    if (!project || !activeMonth || !activeWeek) return undefined
    if (mode === 'month') return monthBounds(activeMonth)
    if (mode === 'week') return { from: activeWeek, to: addDays(activeWeek, 6) }
    return { from: customFrom || project.startDate, to: customTo || project.endDate }
  })()
  const from = project && range ? clampDate(range.from, project.startDate, project.endDate) : ''
  const to = project && range ? clampDate(range.to, project.startDate, project.endDate) : ''
  const span = from && to ? daysBetween(from, to) : 0
  const rangeError = !project
    ? ''
    : to < from
      ? t('اختر فترة صحيحة', 'Choose a valid period')
      : span > MAX_RANGE_DAYS
        ? t(`الحد الأقصى للتصدير الجماعي ${MAX_RANGE_DAYS} يوماً`, `Bulk export is limited to ${MAX_RANGE_DAYS} days`)
        : ''

  const { data: reports, error: reportsError, loading: reportsLoading, reload: reloadReports } = useAsync(
    () => (project && from && to && !rangeError ? api.fetchReports(project.id, from, to) : Promise.resolve(undefined)),
    [project?.id, from, to, rangeError]
  )

  const visibleReports = (reports ?? []).filter((report) => role !== 'SUPERVISOR' || report.workflow === 'APPROVED')

  const exportRange = (format: api.ExportFormat) => {
    if (!project || rangeError) return
    run(format, () => api.downloadRange(project.id, from, to, format), t('تم تجهيز الملف', 'File is ready'))
  }

  const periodLabel = rangeError || !from ? t('اختر فترة', 'Choose a period') : `${formatDate(from)} – ${formatDate(to)}`

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{t('التصدير', 'Exports')}</h1>
          <p className="muted">
            {project ? `${project.name} · ${periodLabel}` : t('اختر مشروعاً لتنزيل تقاريره', 'Choose a project to download its reports')}
          </p>
        </div>
        {role ? <span className="chip">{ROLE_LABELS[role]}</span> : null}
      </div>

      {projectsLoading ? (
        <Spinner />
      ) : projectsError ? (
        <ErrorState message={projectsError} onRetry={reloadProjects} />
      ) : !projects?.length ? (
        <EmptyState icon="download" title={t('لا توجد مشاريع للتصدير', 'No projects to export')}>
          <p className="muted">{t('يظهر التصدير بعد إضافتك إلى مشروع.', 'Exports appear after you are added to a project.')}</p>
        </EmptyState>
      ) : (
        <>
          {copy ? (
            <div className={`alert alert--${copy.tone}`} role="status">
              <Icon name={copy.tone === 'warning' ? 'alert' : 'info'} size={18} />
              <div>
                <strong>{t(copy.titleAr, copy.titleEn)}</strong>
                <p className="small">{t(copy.bodyAr, copy.bodyEn)}</p>
              </div>
            </div>
          ) : null}

          <Card title={t('تصدير الفترة', 'Export a period')}>
            <div className="filters">
              <SelectField
                label={t('المشروع', 'Project')}
                value={projectId}
                onChange={(event) => {
                  const next = projects.find((item) => item.id === event.target.value)
                  setProjectId(event.target.value)
                  setMonth(null)
                  setWeek(null)
                  if (next) {
                    setCustomFrom(next.startDate)
                    setCustomTo(next.endDate)
                  }
                }}
              >
                {projects.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name} · {ROLE_LABELS[item.myRole]}
                  </option>
                ))}
              </SelectField>
              <div className="segmented" role="group" aria-label={t('نوع الفترة', 'Period type')}>
                <button type="button" aria-pressed={mode === 'week'} onClick={() => setMode('week')}>
                  {t('أسبوعي', 'Weekly')}
                </button>
                <button type="button" aria-pressed={mode === 'month'} onClick={() => setMode('month')}>
                  {t('شهري', 'Monthly')}
                </button>
                <button type="button" aria-pressed={mode === 'custom'} onClick={() => setMode('custom')}>
                  {t('فترة مخصصة', 'Custom period')}
                </button>
              </div>
              {mode === 'month' ? (
                <SelectField label={t('الشهر', 'Month')} value={activeMonth} onChange={(event) => setMonth(event.target.value)}>
                  {months.map((key) => (
                    <option key={key} value={key}>
                      {monthTitle(key)}
                    </option>
                  ))}
                </SelectField>
              ) : mode === 'week' ? (
                <SelectField label={t('الأسبوع (السبت – الجمعة)', 'Week (Saturday – Friday)')} value={activeWeek} onChange={(event) => setWeek(event.target.value)}>
                  {weeks.map((start, index) => (
                    <option key={start} value={start}>
                      {t(
                        `الأسبوع ${index + 1}: ${formatDate(start)} – ${formatDate(addDays(start, 6))}`,
                        `Week ${index + 1}: ${formatDate(start)} – ${formatDate(addDays(start, 6))}`
                      )}
                    </option>
                  ))}
                </SelectField>
              ) : (
                <>
                  <label className="field">
                    <span className="field__label">{t('من', 'From')}</span>
                    <input className="input" type="date" value={customFrom} min={project?.startDate} max={project?.endDate} onChange={(event) => setCustomFrom(event.target.value)} />
                  </label>
                  <label className="field">
                    <span className="field__label">{t('إلى', 'To')}</span>
                    <input className="input" type="date" value={customTo} min={project?.startDate} max={project?.endDate} onChange={(event) => setCustomTo(event.target.value)} />
                  </label>
                </>
              )}
            </div>
            <p className="muted small">
              {t(
                `ملف مضغوط، وكل تقرير ملف مستقل. تحويل PDF قد يأخذ وقتاً. الحد الأقصى ${MAX_RANGE_DAYS} يوماً.`,
                `A zip file, with each report as its own file. PDF conversion can take a while. Maximum ${MAX_RANGE_DAYS} days.`
              )}
              {role === 'SUPERVISOR'
                ? t(' الحزمة تقتصر على التقارير المعتمدة.', ' The package includes approved reports only.')
                : t(' غير المعتمد يُعلَّم «مسودة» داخل الملف.', ' Anything not approved is marked "Draft" in the file.')}
            </p>
            {rangeError ? <p className="field__error">{rangeError}</p> : null}
            <div className="toolbar">
              <Button icon="download" disabled={Boolean(rangeError) || !visibleReports.length} busy={busy === 'xlsx'} onClick={() => exportRange('xlsx')}>
                {t('تقارير الفترة Excel', 'Period reports Excel')}
              </Button>
              <Button icon="file" variant="primary" disabled={Boolean(rangeError) || !visibleReports.length} busy={busy === 'pdf'} onClick={() => exportRange('pdf')}>
                {t('تقارير الفترة PDF', 'Period reports PDF')}
              </Button>
            </div>
          </Card>

          <Card title={t(`تقرير واحد${reports ? ` · ${visibleReports.length}` : ''}`, `One report${reports ? ` · ${visibleReports.length}` : ''}`)}>
            {reportsLoading ? (
              <Spinner />
            ) : reportsError ? (
              <ErrorState message={reportsError} onRetry={reloadReports} />
            ) : !visibleReports.length ? (
              <EmptyState
                icon="file"
                title={role === 'SUPERVISOR' ? t('لا توجد تقارير معتمدة في هذه الفترة', 'No approved reports in this period') : t('لا توجد تقارير في هذه الفترة', 'No reports in this period')}
              />
            ) : (
              <ReportTable projectId={projectId} reports={visibleReports} busy={busy} onDownload={(key, action) => run(key, action)} />
            )}
          </Card>

          {user?.isAdmin ? (
            <div className="export-split">
              <Card title={t('سجل الاعتمادات', 'Approval log')}>
                <p className="muted small">
                  {t(
                    'اعتماد وإرجاع وإرسال التقارير، مع الملاحظات والمرفقات، خلال آخر 12 شهراً عبر كل المشاريع. الملف CSV.',
                    'Approvals, returns, and submissions, with notes and attachments, for the last 12 months across every project. The file is CSV.'
                  )}
                </p>
                <Button icon="download" busy={busy === 'audit'} onClick={() => run('audit', () => api.downloadAuditLog(), t('تم تجهيز السجل', 'Log is ready'))}>
                  {t('تنزيل السجل', 'Download log')}
                </Button>
              </Card>
              <Card title={t('قالب التقرير', 'Report template')}>
                {templateLoading ? (
                  <Spinner />
                ) : templateError ? (
                  <ErrorState message={templateError} onRetry={reloadTemplate} />
                ) : (
                  <>
                    <p className="muted small">
                      {template?.custom
                        ? t(
                            `قالب مخصص${template.updatedAt ? ` · آخر تحديث ${formatDateTime(template.updatedAt)}` : ''}`,
                            `Custom template${template.updatedAt ? ` · last updated ${formatDateTime(template.updatedAt)}` : ''}`
                          )
                        : t('القالب الافتراضي المعتمد لكل عمليات التصدير.', 'The default template used for every export.')}
                    </p>
                    <Button icon="download" busy={busy === 'template'} onClick={() => run('template', () => api.downloadTemplate(), t('تم تجهيز القالب', 'Template is ready'))}>
                      {t('تنزيل القالب', 'Download template')}
                    </Button>
                  </>
                )}
              </Card>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}

const ReportTable = ({
  projectId,
  reports,
  busy,
  onDownload,
}: {
  projectId: string
  reports: ReportListItem[]
  busy: string | null
  onDownload: (key: string, action: () => Promise<unknown>) => void
}) => (
  <div className="table-wrap">
    <table className="table">
      <thead>
        <tr>
          <th scope="col">{t('التقرير', 'Report')}</th>
          <th scope="col">{t('التاريخ', 'Date')}</th>
          <th scope="col">{t('الحالة', 'Status')}</th>
          <th scope="col">{t('أعدّه', 'Prepared by')}</th>
          <th scope="col">{t('تنزيل', 'Download')}</th>
        </tr>
      </thead>
      <tbody>
        {reports.map((report) => (
          <tr key={report.id}>
            <td>
              <Link to={`/projects/${projectId}/reports/${report.id}`} className="code-link">
                {report.code}
              </Link>
            </td>
            <td className="num">
              {formatDate(report.reportDate)}
              <span className="muted small"> {printWeekday(activeLocale(), report.reportDate, report.dayName)}</span>
            </td>
            <td>
              <StatusBadge status={report.status} />
            </td>
            <td>{report.preparedByName ?? <span className="muted">—</span>}</td>
            <td>
              <div className="row-actions">
                <IconButton
                  icon="download"
                  label={t(`تنزيل ${report.code} Excel`, `Download ${report.code} Excel`)}
                  disabled={busy === `x${report.id}`}
                  onClick={() => onDownload(`x${report.id}`, () => api.downloadReport(report.id, 'xlsx'))}
                />
                <IconButton
                  icon="file"
                  label={t(`تنزيل ${report.code} PDF`, `Download ${report.code} PDF`)}
                  disabled={busy === `p${report.id}`}
                  onClick={() => onDownload(`p${report.id}`, () => api.downloadReport(report.id, 'pdf'))}
                />
                <IconButton
                  icon="paperclip"
                  label={report.attachmentCount ? t(`مرفقات ${report.code}`, `Attachments for ${report.code}`) : t('لا توجد مرفقات', 'No attachments')}
                  disabled={!report.attachmentCount || busy === `z${report.id}`}
                  onClick={() => onDownload(`z${report.id}`, () => api.downloadReportAttachments(report.id))}
                />
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)
